/**
 * Water security helpers — pure functions only, exported for testing.
 *
 * pm25Advisory drives the "PM2.5 advisory" chip in WaterSecurityHero.
 * Thresholds match PCD's daily Thai-language bulletin (≤25 good,
 * ≤50 moderate, ≤100 starting to affect health, >100 hazardous).
 *
 * damAlertColor and droughtBandColor pull the color/label maps out of
 * the component for testability — the dashboard used to keep these as
 * component-internal consts, so a misaligned threshold could ship
 * silently. They're now exported and covered.
 */

import type { DamReading, DroughtBrief } from "@sikhio/shared";

const PM25_BANDS = [
  { max: 25,  label: "อากาศดี",        color: "var(--good)" },
  { max: 50,  label: "ปานกลาง",        color: "var(--gold)" },
  { max: 100, label: "เริ่มมีผลกระทบ", color: "var(--warn)" },
  { max: Infinity, label: "อันตราย",   color: "var(--bad)"  },
] as const;

export function pm25Advisory(pm25: number | null | undefined): { label: string; color: string } | null {
  if (pm25 == null || !Number.isFinite(pm25)) return null;
  for (const b of PM25_BANDS) {
    if (pm25 <= b.max) return { label: b.label, color: b.color };
  }
  return null;
}

export const DAM_COLOR: Record<DamReading["alertLevel"], string> = {
  "critical-low":  "var(--bad)",
  "low":           "var(--bad)",
  "watch-low":     "var(--gold)",
  "normal":        "var(--good)",
  "watch-high":    "var(--gold)",
  "high":          "var(--warn)",
  "critical-high": "var(--bad)",
};

export const DAM_LABEL_TH: Record<DamReading["alertLevel"], string> = {
  "critical-low":  "วิกฤตน้ำน้อย",
  "low":           "น้ำน้อย",
  "watch-low":     "เฝ้าระวังภัยแล้ง",
  "normal":        "ปกติ",
  "watch-high":    "เฝ้าระวังน้ำมาก",
  "high":          "น้ำมาก",
  "critical-high": "วิกฤตน้ำมาก",
};

export const DROUGHT_COLOR: Record<DroughtBrief["band"], string> = {
  normal:   "var(--good)",
  mild:     "var(--gold)",
  moderate: "var(--warn)",
  severe:   "var(--bad)",
};

export const DROUGHT_LABEL_TH: Record<DroughtBrief["band"], string> = {
  normal:   "ปกติ",
  mild:     "เริ่มแล้ง",
  moderate: "แล้งปานกลาง",
  severe:   "แล้งรุนแรง",
};
